var __jsx = React.createElement;
import React, { useEffect, useRef, useState } from "react";
import AwesomeIcon from "../../AwesomeIcon";
import { faSpinner, faThumbsUp, faThumbsDown, faCopy, faArrowsRotate, faStars } from "@fortawesome/pro-regular-svg-icons";
import PatientSummary from "../PatientSummary";
import { useCopilotChat } from "@copilotkit/react-core";
import { Role, TextMessage } from "@copilotkit/runtime-client-gql";
import { trackEvent } from "../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../utils/mixpanelEvents";
var AssistantMessageHeader = function AssistantMessageHeader() {
  return /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-x-2 rounded-t-lg px-4 py-2 text-md w-fit font-semibold text-teal-200 dark:text-teal-300 bg-gray-500 dark:bg-gray-800"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faStars,
    size: "lg",
    className: "text-teal-200 dark:text-teal-300"
  }), "AI Assistant");
};
var FeedbackControls = function FeedbackControls(_ref) {
  var message = _ref.message,
    onThumbsUp = _ref.onThumbsUp,
    onThumbsDown = _ref.onThumbsDown,
    onCopy = _ref.onCopy,
    onRegenerate = _ref.onRegenerate,
    _ref$showCopy = _ref.showCopy,
    showCopy = _ref$showCopy === void 0 ? true : _ref$showCopy,
    _ref$showRegenerate = _ref.showRegenerate,
    showRegenerate = _ref$showRegenerate === void 0 ? true : _ref$showRegenerate,
    _ref$iconClassName = _ref.iconClassName,
    iconClassName = _ref$iconClassName === void 0 ? "" : _ref$iconClassName,
    _ref$centerCard = _ref.centerCard,
    centerCard = _ref$centerCard === void 0 ? false : _ref$centerCard;
  var _useState = useState(""),
    feedback = _useState[0],
    setFeedback = _useState[1];
  var feedbackTimeout = useRef(null);
  var showFeedback = function showFeedback(msg) {
    setFeedback(msg);
    if (feedbackTimeout.current) clearTimeout(feedbackTimeout.current);
    feedbackTimeout.current = setTimeout(function () {
      return setFeedback("");
    }, 1800);
  };
  var controls = /*#__PURE__*/React.createElement("div", {
    className: centerCard ? "flex flex-row space-x-6 items-center justify-center" : "flex space-x-4 items-center border-t border-gray-400/20 mt-3 pt-2 w-full"
  }, /*#__PURE__*/React.createElement("button", {
    "aria-label": "Thumbs Up",
    className: "hover:text-teal-400 focus:outline-none transition-transform transition-colors duration-150 ease-in-out hover:scale-110 hover:-translate-y-0.5 hover:rotate-[-8deg]",
    onClick: function onClick() {
      onThumbsUp && onThumbsUp(message);
      trackEvent(MIXPANEL_EVENTS.ASSISTANT_FEEDBACK, {
        action: "thumbs_up"
      });
      showFeedback("Thank you for your feedback!");
    },
    type: "button"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faThumbsUp,
    size: "sm",
    className: iconClassName
  })), /*#__PURE__*/React.createElement("button", {
    "aria-label": "Thumbs Down",
    className: "hover:text-red-400 focus:outline-none transition-transform transition-colors duration-150 ease-in-out hover:scale-110 hover:-translate-y-0.5 hover:rotate-[8deg]",
    onClick: function onClick() {
      onThumbsDown && onThumbsDown(message);
      trackEvent(MIXPANEL_EVENTS.ASSISTANT_FEEDBACK, {
        action: "thumbs_down"
      });
      showFeedback("Thank you for your feedback!");
    },
    type: "button"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faThumbsDown,
    size: "sm",
    className: iconClassName
  })), showCopy && /*#__PURE__*/React.createElement("button", {
    "aria-label": "Copy Message",
    className: "hover:text-teal-400 focus:outline-none transition-transform transition-colors duration-150 ease-in-out hover:scale-110 hover:-translate-y-0.5 hover:rotate-[-6deg]",
    onClick: function onClick() {
      onCopy && onCopy(message);
      if (message) {
        var _navigator$clipboard;
        (_navigator$clipboard = navigator.clipboard) === null || _navigator$clipboard === void 0 || _navigator$clipboard.writeText(message);
      }
      trackEvent(MIXPANEL_EVENTS.ASSISTANT_FEEDBACK, {
        action: "copy"
      });
      showFeedback("Copied!");
    },
    type: "button"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faCopy,
    size: "sm",
    className: iconClassName
  })), showRegenerate && /*#__PURE__*/React.createElement("button", {
    "aria-label": "Regenerate Message",
    className: "hover:text-teal-400 focus:outline-none transition-transform transition-colors duration-150 ease-in-out hover:scale-110 hover:-translate-y-0.5 hover:rotate-[6deg]",
    onClick: function onClick() {
      onRegenerate && onRegenerate();
      trackEvent(MIXPANEL_EVENTS.ASSISTANT_FEEDBACK, {
        action: "regenerate"
      });
      showFeedback("Regenerating...");
    },
    type: "button"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faArrowsRotate,
    size: "sm",
    className: iconClassName
  })), feedback && /*#__PURE__*/React.createElement("span", {
    className: "ml-2 text-teal-300 dark:text-teal-200 animate-fade-in-out text-sm font-medium",
    style: {
      minWidth: 80
    }
  }, feedback));
  if (centerCard) {
    return /*#__PURE__*/React.createElement("div", {
      className: "flex flex-col items-start w-full"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bg-gray-50 dark:bg-gray-800/40 transition-colors rounded-md py-2 px-4 mt-3 w-fit flex flex-col items-start"
    }, controls));
  }
  return controls;
};
var CustomAssistantMessage = function CustomAssistantMessage(props) {
  var message = props.message,
    isLoading = props.isLoading,
    subComponent = props.subComponent,
    onThumbsUp = props.onThumbsUp,
    onThumbsDown = props.onThumbsDown,
    onCopy = props.onCopy,
    onRegenerate = props.onRegenerate;
  var loadingKeys = ["Analyzing clinical context...", "Compiling patient data...", "Processing medical records...", "Interpreting inputs...", "Reviewing patient information...", "Gathering relevant details...", "Synchronizing with medical database..."];
  var currentIndex = Math.floor(Math.random() * loadingKeys.length);
  var isPatientSummary = message === null || message === void 0 ? void 0 : message.includes("<<<PATIENT_SUMMARY_JSON>>>");
  var _useCopilotChat = useCopilotChat(),
    visibleMessages = _useCopilotChat.visibleMessages,
    setMessages = _useCopilotChat.setMessages,
    appendMessage = _useCopilotChat.appendMessage;
  useEffect(function () {
    var _visibleMessages;
    if (((_visibleMessages = visibleMessages[visibleMessages.length - 1]) === null || _visibleMessages === void 0 ? void 0 : _visibleMessages.content) === "❌ An error occurred. Please try again.") {
      setMessages(visibleMessages.slice(0, -1));
      setTimeout(function () {
        appendMessage(new TextMessage({
          content: "Please start a new chat to continue.",
          role: Role.Assistant
        }), {
          followUp: false
        });
      }, 1000);
    }
  }, [visibleMessages]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, (message || subComponent) && /*#__PURE__*/React.createElement("div", {
    className: "flex items-start mb-4 last:mb-0 justify-start"
  }, /*#__PURE__*/React.createElement("div", {
    className: "max-w-[90%] w-full"
  }, isPatientSummary ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AssistantMessageHeader, null), /*#__PURE__*/React.createElement(PatientSummary, {
    message: message !== null && message !== void 0 ? message : ""
  }), /*#__PURE__*/React.createElement(FeedbackControls, {
    message: message !== null && message !== void 0 ? message : "",
    onThumbsUp: onThumbsUp,
    onThumbsDown: onThumbsDown,
    showCopy: false,
    showRegenerate: false,
    iconClassName: "text-gray-700 dark:text-gray-200 hover:text-inherit dark:hover:text-inherit",
    centerCard: true
  })) : subComponent ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AssistantMessageHeader, null), subComponent, /*#__PURE__*/React.createElement(FeedbackControls, {
    message: message !== null && message !== void 0 ? message : "",
    onThumbsUp: onThumbsUp,
    onThumbsDown: onThumbsDown,
    showCopy: false,
    showRegenerate: false,
    iconClassName: "text-gray-700 dark:text-gray-200 hover:text-inherit dark:hover:text-inherit",
    centerCard: true
  })) : /*#__PURE__*/React.createElement("div", {
    className: "inline-block"
  }, /*#__PURE__*/React.createElement(AssistantMessageHeader, null), /*#__PURE__*/React.createElement("div", {
    className: "rounded-b-lg px-5 py-4 text-gray-700 dark:text-white bg-gray-100 dark:bg-gray-800"
  }, /*#__PURE__*/React.createElement("p", {
    className: "whitespace-pre-wrap"
  }, message), /*#__PURE__*/React.createElement(FeedbackControls, {
    message: message !== null && message !== void 0 ? message : "",
    onThumbsUp: onThumbsUp,
    onThumbsDown: onThumbsDown,
    onCopy: onCopy,
    onRegenerate: onRegenerate,
    showCopy: true,
    showRegenerate: true,
    iconClassName: "",
    centerCard: false
  }))))), isLoading && /*#__PURE__*/React.createElement("div", {
    className: "flex space-x-3 dark:text-gray-400 text-gray-600 pl-8"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faSpinner,
    size: "lg",
    className: "animate-spin"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-md italic"
  }, loadingKeys[currentIndex])));
};
export default CustomAssistantMessage;